/** @module @liquidefy/code-editor/terminal/terminal-ai @version 1.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  CommandSuggestion,
  ErrorAnalysis,
  SuggestedSolution,
  NLContext,
  NLCommandResult,
  AIPrivacySettings,
  CommandHistoryEntry,
  CommandHistoryState,
  TerminalSessionState,
  ShellType,
  PTYPlatform,
  EditorResult,
} from '../../code-editor-types.js';

import {
  ok,
  err,
  now,
  DANGEROUS_COMMANDS,
  DESTRUCTIVE_PATTERNS,
  assessCommandRisk,
  isDestructiveCommand,
  buildNLContext as engineBuildNLContext,
  scoreSuggestion as engineScoreSuggestion,
  rankSuggestions as engineRankSuggestions,
} from '../../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 COMMAND SUGGESTIONS — generateSuggestions, scoreAndRank, suggestFromHistory, suggestFromContext
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Generate command suggestions based on history and context.
 */
export function generateSuggestions(
  history: CommandHistoryState,
  context: NLContext,
  count?: number
): CommandSuggestion[] {
  const maxCount = count ?? 10;
  const fromHistory = suggestFromHistory(history, '');
  const fromContext = suggestFromContext(context.cwd, context.recentCommands);

  const combined = [...fromHistory, ...fromContext];
  const unique = deduplicateSuggestions(combined);
  const ranked = scoreAndRank(unique);

  return ranked.slice(0, maxCount);
}

/**
 * Score and rank suggestions by confidence and relevance.
 */
export function scoreAndRank(suggestions: CommandSuggestion[]): CommandSuggestion[] {
  return [...suggestions].sort((a, b) => {
    // Primary: confidence descending
    if (b.confidence !== a.confidence) return b.confidence - a.confidence;
    // Secondary: safe commands first
    const riskOrder: Record<string, number> = { safe: 0, moderate: 1, dangerous: 2 };
    return (riskOrder[a.risk] ?? 1) - (riskOrder[b.risk] ?? 1);
  });
}

/**
 * Suggest commands from history based on prefix/subsequence matching.
 */
export function suggestFromHistory(
  history: CommandHistoryState,
  currentInput: string
): CommandSuggestion[] {
  const input = currentInput.toLowerCase().trim();
  const freq = new Map<string, { count: number; lastUsed: number; exitCodes: number[] }>();

  for (const entry of history.entries) {
    const cmd = entry.command.trim();
    const existing = freq.get(cmd);
    if (existing) {
      existing.count++;
      existing.lastUsed = Math.max(existing.lastUsed, entry.timestamp);
      if (entry.exitCode !== undefined) existing.exitCodes.push(entry.exitCode);
    } else {
      freq.set(cmd, {
        count: 1,
        lastUsed: entry.timestamp,
        exitCodes: entry.exitCode !== undefined ? [entry.exitCode] : [],
      });
    }
  }

  const suggestions: CommandSuggestion[] = [];

  for (const [command, stats] of freq) {
    const lower = command.toLowerCase();

    // Match by prefix or subsequence
    let matches = false;
    if (input.length === 0) {
      matches = true; // Show all frequent commands when no input
    } else if (lower.startsWith(input)) {
      matches = true;
    } else if (isSubsequence(input, lower)) {
      matches = true;
    }

    if (matches) {
      const successRate = stats.exitCodes.length > 0
        ? stats.exitCodes.filter(c => c === 0).length / stats.exitCodes.length
        : 0.5;

      const confidence = Math.min(1.0,
        (stats.count / Math.max(history.entries.length, 1)) * 0.4 +
        successRate * 0.3 +
        (input.length > 0 && lower.startsWith(input) ? 0.3 : 0.1)
      );

      const risk = assessCommandRisk(command);

      suggestions.push({
        command,
        description: `Used ${stats.count} time(s)`,
        confidence,
        source: 'history',
        risk,
        isPersonalized: true,
      });
    }
  }

  return suggestions;
}

/**
 * Suggest commands from the current context (cwd, recent commands).
 */
export function suggestFromContext(
  cwd: string,
  recentCommands: string[]
): CommandSuggestion[] {
  const suggestions: CommandSuggestion[] = [];

  // Suggest common follow-up commands
  if (recentCommands.length > 0) {
    const last = recentCommands[recentCommands.length - 1]?.trim() ?? '';

    if (last.startsWith('git add')) {
      suggestions.push(makeSuggestion('git commit -m ""', 'Commit staged changes', 0.8, 'context'));
      suggestions.push(makeSuggestion('git status', 'Check repository status', 0.7, 'context'));
    } else if (last.startsWith('git commit')) {
      suggestions.push(makeSuggestion('git push', 'Push commits to remote', 0.8, 'context'));
    } else if (last.startsWith('npm install') || last.startsWith('yarn add') || last.startsWith('pnpm add')) {
      suggestions.push(makeSuggestion('npm run dev', 'Start development server', 0.6, 'context'));
      suggestions.push(makeSuggestion('npm test', 'Run tests', 0.5, 'context'));
    } else if (last.startsWith('cd ')) {
      suggestions.push(makeSuggestion('ls -la', 'List directory contents', 0.7, 'context'));
      suggestions.push(makeSuggestion('pwd', 'Print working directory', 0.5, 'context'));
    } else if (last === 'docker build' || last.startsWith('docker build ')) {
      suggestions.push(makeSuggestion('docker run', 'Run the built image', 0.7, 'context'));
    }
  }

  // Suggest based on common project files
  if (cwd.includes('node_modules') || cwd.includes('package.json')) {
    suggestions.push(makeSuggestion('npm run build', 'Build the project', 0.4, 'context'));
  }

  return suggestions;
}

/**
 * Common commands mapped to task descriptions.
 */
export const COMMON_COMMANDS: Record<string, string> = {
  'ls': 'List files in directory',
  'cd': 'Change directory',
  'pwd': 'Print working directory',
  'mkdir': 'Create directory',
  'rm': 'Remove file or directory',
  'cp': 'Copy file or directory',
  'mv': 'Move/rename file or directory',
  'cat': 'Display file contents',
  'grep': 'Search text patterns',
  'find': 'Find files',
  'chmod': 'Change file permissions',
  'chown': 'Change file ownership',
  'ps': 'List processes',
  'kill': 'Terminate process',
  'top': 'Monitor system processes',
  'df': 'Disk usage',
  'du': 'Directory size',
  'tar': 'Archive files',
  'curl': 'Transfer data from URLs',
  'wget': 'Download files',
  'ssh': 'Secure shell connection',
  'scp': 'Secure copy',
  'git': 'Version control',
  'docker': 'Container management',
  'npm': 'Node package manager',
  'yarn': 'Node package manager',
  'pnpm': 'Fast Node package manager',
  'make': 'Build automation',
  'python': 'Python interpreter',
  'node': 'Node.js runtime',
  'cargo': 'Rust package manager',
  'go': 'Go toolchain',
};

/**
 * Common flags for popular commands.
 */
export const COMMON_FLAGS: Record<string, Record<string, string>> = {
  'ls': { '-l': 'Long format', '-a': 'Show hidden', '-h': 'Human readable sizes', '-R': 'Recursive' },
  'grep': { '-r': 'Recursive', '-i': 'Case insensitive', '-n': 'Line numbers', '-l': 'Files only', '-v': 'Invert match' },
  'find': { '-name': 'Match name', '-type': 'File type', '-size': 'File size', '-mtime': 'Modified time' },
  'git': { 'status': 'Show status', 'log': 'Show history', 'diff': 'Show changes', 'branch': 'List branches' },
  'docker': { 'ps': 'List containers', 'images': 'List images', 'run': 'Run container', 'build': 'Build image' },
  'npm': { 'install': 'Install packages', 'run': 'Run script', 'test': 'Run tests', 'build': 'Build project' },
  'curl': { '-X': 'HTTP method', '-H': 'Header', '-d': 'Data', '-o': 'Output file', '-s': 'Silent' },
};

// ═══════════════════════════════════════════════════════════════════════════════
// §3 ERROR ANALYSIS — analyzeError, categorizeError, suggestFixes, explainError
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Analyze a terminal error and generate an explanation with suggested fixes.
 */
export function analyzeError(
  error: string,
  _context: NLContext
): ErrorAnalysis {
  const category = categorizeError(error);
  const explanation = explainError(error);
  const solutions = suggestFixes(error, 'bash');
  const relatedCommands = extractRelatedCommands(error);

  return {
    error,
    explanation,
    solutions,
    commonCause: COMMON_ERRORS[category] ?? 'Unknown cause',
    relatedCommands,
  };
}

/**
 * Categorize an error into a known category.
 */
export function categorizeError(error: string): string {
  const lower = error.toLowerCase();

  if (/permission denied|access denied|eacces/i.test(lower)) return 'permission';
  if (/not found|no such file|enoent|command not found/i.test(lower)) return 'not-found';
  if (/syntax error|unexpected token|parse error/i.test(lower)) return 'syntax';
  if (/connection refused|econnrefused|etimedout|network/i.test(lower)) return 'network';
  if (/no space left|disk full|enospc/i.test(lower)) return 'resource';
  if (/out of memory|enomem|heap|oom/i.test(lower)) return 'memory';
  if (/already exists|eexist|conflict/i.test(lower)) return 'conflict';
  if (/timeout|timed out/i.test(lower)) return 'timeout';
  if (/segmentation fault|segfault|sigsegv/i.test(lower)) return 'crash';
  if (/dependency|module not found|cannot find module/i.test(lower)) return 'dependency';
  if (/authentication|unauthorized|403|401/i.test(lower)) return 'auth';
  if (/port.*in use|eaddrinuse/i.test(lower)) return 'port-conflict';

  return 'unknown';
}

/**
 * Suggest fixes for a given error in a specific shell.
 */
export function suggestFixes(error: string, _shell: string): SuggestedSolution[] {
  const category = categorizeError(error);
  const solutions: SuggestedSolution[] = [];

  switch (category) {
    case 'permission':
      solutions.push({
        command: 'sudo !!',
        description: 'Retry with elevated privileges',
        confidence: 0.7,
        risk: 'moderate',
      });
      solutions.push({
        command: 'chmod +x <file>',
        description: 'Make the file executable',
        confidence: 0.5,
        risk: 'safe',
      });
      break;

    case 'not-found':
      if (/command not found/i.test(error)) {
        const cmd = error.match(/(?:command not found|not found).*?:\s*(\S+)/i)?.[1] ?? '';
        solutions.push({
          command: `which ${cmd}`,
          description: `Check if '${cmd}' is installed`,
          confidence: 0.8,
          risk: 'safe',
        });
        solutions.push({
          command: `apt-get install ${cmd} || brew install ${cmd}`,
          description: `Install '${cmd}'`,
          confidence: 0.5,
          risk: 'moderate',
        });
      } else {
        solutions.push({
          command: 'ls -la',
          description: 'List files to verify the path',
          confidence: 0.6,
          risk: 'safe',
        });
      }
      break;

    case 'network':
      solutions.push({
        command: 'ping -c 3 google.com',
        description: 'Check internet connectivity',
        confidence: 0.7,
        risk: 'safe',
      });
      solutions.push({
        command: 'netstat -tlnp',
        description: 'Check listening ports',
        confidence: 0.5,
        risk: 'safe',
      });
      break;

    case 'resource':
      solutions.push({
        command: 'df -h',
        description: 'Check available disk space',
        confidence: 0.8,
        risk: 'safe',
      });
      solutions.push({
        command: 'du -sh * | sort -rh | head -10',
        description: 'Find largest files/directories',
        confidence: 0.7,
        risk: 'safe',
      });
      break;

    case 'memory':
      solutions.push({
        command: 'free -h',
        description: 'Check available memory',
        confidence: 0.7,
        risk: 'safe',
      });
      solutions.push({
        command: 'ps aux --sort=-%mem | head -10',
        description: 'Find memory-heavy processes',
        confidence: 0.6,
        risk: 'safe',
      });
      break;

    case 'port-conflict':
      solutions.push({
        command: 'lsof -i :<port>',
        description: 'Find process using the port',
        confidence: 0.8,
        risk: 'safe',
      });
      solutions.push({
        command: 'kill <pid>',
        description: 'Kill the process using the port',
        confidence: 0.5,
        risk: 'moderate',
      });
      break;

    case 'dependency':
      solutions.push({
        command: 'npm install',
        description: 'Reinstall dependencies',
        confidence: 0.7,
        risk: 'safe',
      });
      solutions.push({
        command: 'rm -rf node_modules && npm install',
        description: 'Clean reinstall dependencies',
        confidence: 0.6,
        risk: 'moderate',
      });
      break;

    default:
      solutions.push({
        command: 'echo $?',
        description: 'Check exit code of last command',
        confidence: 0.4,
        risk: 'safe',
      });
  }

  return solutions;
}

/**
 * Generate a human-readable explanation for an error.
 */
export function explainError(error: string): string {
  const category = categorizeError(error);
  const explanations: Record<string, string> = {
    'permission': 'The operation was denied because the current user lacks the required permissions.',
    'not-found': 'The requested file, directory, or command could not be found at the specified path.',
    'syntax': 'The command contains a syntax error that prevents it from being parsed correctly.',
    'network': 'A network operation failed, possibly due to connectivity issues or a refused connection.',
    'resource': 'The system has insufficient disk space to complete the operation.',
    'memory': 'The process ran out of available memory.',
    'conflict': 'The operation conflicts with an existing resource (file, process, or lock).',
    'timeout': 'The operation exceeded the allowed time limit.',
    'crash': 'The process crashed due to a memory access violation.',
    'dependency': 'A required dependency or module could not be found or loaded.',
    'auth': 'Authentication failed or the request lacks valid credentials.',
    'port-conflict': 'The specified port is already in use by another process.',
  };

  return explanations[category] ?? 'An unexpected error occurred. Check the error message for details.';
}

/**
 * Common error patterns mapped to their typical causes.
 */
export const COMMON_ERRORS: Record<string, string> = {
  'permission': 'Insufficient file or process permissions',
  'not-found': 'Missing file, directory, or command',
  'syntax': 'Invalid command syntax or shell grammar',
  'network': 'Network connectivity or DNS resolution failure',
  'resource': 'Disk space exhaustion',
  'memory': 'Process memory limit exceeded',
  'conflict': 'Resource already exists or is locked',
  'timeout': 'Operation exceeded time limit',
  'crash': 'Segmentation fault or invalid memory access',
  'dependency': 'Missing package or module dependency',
  'auth': 'Invalid or expired credentials',
  'port-conflict': 'Port already bound by another process',
  'unknown': 'Unrecognized error pattern',
};

/**
 * Error category names.
 */
export const ERROR_CATEGORIES: string[] = [
  'permission', 'not-found', 'syntax', 'network', 'resource',
  'memory', 'conflict', 'timeout', 'crash', 'dependency',
  'auth', 'port-conflict', 'unknown',
];

// ═══════════════════════════════════════════════════════════════════════════════
// §4 COMMAND RISK ASSESSMENT — assessRisk, isDestructive, isSudo, network, disk, privilege
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Assess the risk level of a command.
 */
export function assessRisk(command: string): CommandSuggestion['risk'] {
  return assessCommandRisk(command);
}

/**
 * Check if a command is destructive.
 */
export function isDestructive(command: string): boolean {
  return isDestructiveCommand(command);
}

/**
 * Check if a command uses sudo or equivalent.
 */
export function isSudo(command: string): boolean {
  const lower = command.trim().toLowerCase();
  return SUDO_VARIANTS.some(s => lower.startsWith(s));
}

/**
 * Check if a command involves network operations.
 */
export function isNetworkSensitive(command: string): boolean {
  const patterns = [
    /\bcurl\b/, /\bwget\b/, /\bssh\b/, /\bscp\b/, /\brsync\b/,
    /\bnc\b/, /\bnetcat\b/, /\btelnet\b/, /\bftp\b/, /\bsftp\b/,
    /\bnmap\b/, /\bdig\b/, /\bhost\b/, /\bping\b/, /\btraceroute\b/,
    /\biptables\b/, /\bufw\b/, /\bfirewall-cmd\b/,
  ];
  return patterns.some(p => p.test(command));
}

/**
 * Check if a command involves heavy disk I/O.
 */
export function isDiskIntensive(command: string): boolean {
  const patterns = [
    /\bdd\b/, /\btar\b.*\b[cxz]\b/, /\brsync\b/, /\bcp\s+-r\b/,
    /\bfind\b.*\-exec\b/, /\bdu\b/, /\bchmod\s+-R\b/, /\bchown\s+-R\b/,
    /\brm\s+-rf?\s+\//, /\bmkfs\b/, /\bfdisk\b/, /\bparted\b/,
  ];
  return patterns.some(p => p.test(command));
}

/**
 * Check if a command involves privilege escalation.
 */
export function isPrivilegeEscalation(command: string): boolean {
  const patterns = [
    /\bsudo\b/, /\bsu\b\s/, /\bsu$/, /\bdoas\b/, /\bpkexec\b/,
    /\bchmod\s+[ugo]*\+s\b/, /\bsetuid\b/, /\bcapabilities\b/,
  ];
  return patterns.some(p => p.test(command));
}

/**
 * Dangerous commands that should trigger warnings.
 */
export { DANGEROUS_COMMANDS };

/**
 * Patterns for destructive operations.
 */
export { DESTRUCTIVE_PATTERNS };

/**
 * Sudo and equivalent privilege escalation commands.
 */
export const SUDO_VARIANTS: string[] = [
  'sudo ', 'sudo\t', 'doas ', 'pkexec ', 'su -c ',
  'runas ', 'gsudo ',
];

/**
 * Explain why a command is risky.
 */
export function riskExplanation(
  command: string,
  risk: CommandSuggestion['risk']
): string {
  if (risk === 'safe') return 'This command is safe to execute.';

  const reasons: string[] = [];
  if (isSudo(command)) reasons.push('uses elevated privileges');
  if (isDestructive(command)) reasons.push('may permanently delete data');
  if (isNetworkSensitive(command)) reasons.push('accesses the network');
  if (isDiskIntensive(command)) reasons.push('performs heavy disk operations');
  if (isPrivilegeEscalation(command)) reasons.push('escalates privileges');

  if (reasons.length === 0) {
    return risk === 'dangerous'
      ? 'This command may have irreversible effects.'
      : 'This command should be used with caution.';
  }

  return `This command ${reasons.join(', ')}.`;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 NATURAL LANGUAGE — buildNLContext, parseNLQuery, nlToCommands, INTENT_MAP, expandTemplate
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Build a natural language context from the current session state.
 */
export function buildNLContext(
  session: TerminalSessionState,
  cwd: string,
  platform: PTYPlatform
): NLContext {
  return engineBuildNLContext(session, cwd, platform);
}

/**
 * Parse a natural language query to extract intent and entities.
 */
export function parseNLQuery(
  query: string
): { intent: string; entities: Record<string, string> } {
  const lower = query.toLowerCase().trim();
  const entities: Record<string, string> = {};

  // Extract quoted strings as arguments
  const quoted = lower.match(/"([^"]+)"|'([^']+)'/g);
  if (quoted) {
    for (let i = 0; i < quoted.length; i++) {
      entities[`arg${i}`] = quoted[i]!.replace(/['"]/g, '');
    }
  }

  // Detect intent
  for (const [pattern, intent] of Object.entries(INTENT_PATTERNS)) {
    if (new RegExp(pattern, 'i').test(lower)) {
      // Extract entities based on intent
      extractIntentEntities(lower, intent, entities);
      return { intent, entities };
    }
  }

  return { intent: 'unknown', entities };
}

/**
 * Convert a natural language query to command suggestions.
 */
export function nlToCommands(
  query: string,
  context: NLContext
): NLCommandResult[] {
  const parsed = parseNLQuery(query);
  const template = INTENT_MAP[parsed.intent];

  if (!template) {
    return [{
      command: query,
      explanation: 'Could not parse the query into a specific command',
      confidence: 0.2,
      alternatives: [],
    }];
  }

  const command = expandTemplate(template, parsed.entities);
  const alternatives = getAlternativeCommands(parsed.intent, context);

  return [{
    command,
    explanation: `Translated from: "${query}"`,
    confidence: 0.7,
    alternatives,
  }];
}

/**
 * Mapping of intents to command templates.
 */
export const INTENT_MAP: Record<string, string> = {
  'list-files': 'ls -la {path}',
  'find-file': 'find . -name "{name}"',
  'search-text': 'grep -r "{pattern}" {path}',
  'create-directory': 'mkdir -p {name}',
  'remove-file': 'rm {name}',
  'copy-file': 'cp {source} {destination}',
  'move-file': 'mv {source} {destination}',
  'show-file': 'cat {name}',
  'edit-file': '$EDITOR {name}',
  'disk-usage': 'du -sh {path}',
  'process-list': 'ps aux',
  'kill-process': 'kill {pid}',
  'network-check': 'ping -c 3 {host}',
  'port-check': 'lsof -i :{port}',
  'git-status': 'git status',
  'git-commit': 'git commit -m "{message}"',
  'git-push': 'git push',
  'git-pull': 'git pull',
  'install-package': 'npm install {name}',
  'run-tests': 'npm test',
  'start-server': 'npm run dev',
  'docker-list': 'docker ps',
  'docker-build': 'docker build -t {name} .',
  'docker-run': 'docker run {name}',
  'system-info': 'uname -a',
  'memory-usage': 'free -h',
  'disk-space': 'df -h',
};

/**
 * Expand a template by replacing {key} with entity values.
 */
export function expandTemplate(
  template: string,
  entities: Record<string, string>
): string {
  let result = template;
  for (const [key, value] of Object.entries(entities)) {
    result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
  }
  // Remove unfilled placeholders
  result = result.replace(/\{[^}]+\}/g, '').replace(/\s{2,}/g, ' ').trim();
  return result;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 PRIVACY — createPrivacyConfig, shouldRedact, redactSensitive, anonymizeHistory, patterns, isCommandSafe
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create an AI privacy configuration with defaults.
 */
export function createPrivacyConfig(
  overrides?: Partial<AIPrivacySettings>
): AIPrivacySettings {
  return {
    storeHistory: overrides?.storeHistory ?? true,
    shareData: overrides?.shareData ?? false,
    anonymizeData: overrides?.anonymizeData ?? true,
    retentionDays: overrides?.retentionDays ?? 30,
  };
}

/**
 * Check if a command should be redacted based on privacy settings.
 */
export function shouldRedact(command: string, settings: AIPrivacySettings): boolean {
  if (!settings.anonymizeData) return false;
  return REDACTION_PATTERNS.some(p => p.test(command));
}

/**
 * Redact sensitive data (passwords, tokens, keys) from a command string.
 */
export function redactSensitive(command: string): string {
  let result = command;

  // Redact password flags
  result = result.replace(/(-p\s*|--password[=\s])(\S+)/g, '$1[REDACTED]');
  // Redact tokens
  result = result.replace(/(token[=\s:])\s*(\S+)/gi, '$1[REDACTED]');
  // Redact API keys
  result = result.replace(/(api[_-]?key[=\s:])\s*(\S+)/gi, '$1[REDACTED]');
  // Redact secrets
  result = result.replace(/(secret[=\s:])\s*(\S+)/gi, '$1[REDACTED]');
  // Redact bearer tokens
  result = result.replace(/(bearer\s+)(\S+)/gi, '$1[REDACTED]');
  // Redact Authorization headers
  result = result.replace(/(Authorization:\s*)(\S+)/gi, '$1[REDACTED]');
  // Redact AWS keys
  result = result.replace(/(AKIA[A-Z0-9]{16})/g, '[REDACTED_AWS_KEY]');
  // Redact base64 secrets in env vars
  result = result.replace(/([A-Z_]+=)([A-Za-z0-9+/]{40,}=*)/g, '$1[REDACTED]');

  return result;
}

/**
 * Anonymize command history by redacting sensitive data from all entries.
 */
export function anonymizeHistory(
  history: CommandHistoryEntry[]
): CommandHistoryEntry[] {
  return history.map(entry => ({
    ...entry,
    command: redactSensitive(entry.command),
  }));
}

/**
 * Patterns that indicate sensitive data in commands.
 */
export const REDACTION_PATTERNS: RegExp[] = [
  /password/i,
  /secret/i,
  /token/i,
  /api[_-]?key/i,
  /bearer/i,
  /authorization/i,
  /credentials/i,
  /private[_-]?key/i,
  /access[_-]?key/i,
  /AKIA[A-Z0-9]{16}/,
  /ssh-rsa\s/,
  /BEGIN\s+(RSA|DSA|EC|OPENSSH)\s+PRIVATE/,
];

/**
 * Check if a command is safe (contains no sensitive arguments).
 */
export function isCommandSafe(command: string): boolean {
  return !REDACTION_PATTERNS.some(p => p.test(command));
}

// ═══════════════════════════════════════════════════════════════════════════════
// INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

const INTENT_PATTERNS: Record<string, string> = {
  'list|show|display.*files|directory|folder': 'list-files',
  'find|locate|search.*file': 'find-file',
  'search|grep|look for.*text|string|pattern': 'search-text',
  'create|make|new.*dir|folder': 'create-directory',
  'delete|remove|rm.*file': 'remove-file',
  'copy|cp.*file': 'copy-file',
  'move|rename|mv.*file': 'move-file',
  'show|display|print|cat.*file|content': 'show-file',
  'edit|open|modify.*file': 'edit-file',
  'disk|storage.*usage|space|size': 'disk-usage',
  'process|running|ps': 'process-list',
  'kill|stop|terminate.*process': 'kill-process',
  'ping|check.*network|connection|connectivity': 'network-check',
  'port|listen|binding': 'port-check',
  'git.*status|changes': 'git-status',
  'commit|save.*changes': 'git-commit',
  'push|upload.*git|changes': 'git-push',
  'pull|fetch|download.*git|changes': 'git-pull',
  'install.*package|dependency': 'install-package',
  'run.*test|spec': 'run-tests',
  'start.*server|dev': 'start-server',
  'docker.*list|container|running': 'docker-list',
  'docker.*build': 'docker-build',
  'docker.*run|start': 'docker-run',
  'system.*info|version|os': 'system-info',
  'memory.*usage|free|available': 'memory-usage',
  'disk.*space|free|available': 'disk-space',
};

function extractIntentEntities(
  query: string,
  intent: string,
  entities: Record<string, string>
): void {
  // Extract file/directory names
  const pathMatch = query.match(/(?:in|at|from|to)\s+([/~][\w/.+-]+|[\w.+-]+)/i);
  if (pathMatch) {
    entities['path'] = pathMatch[1]!;
    entities['name'] = pathMatch[1]!;
  }

  // Extract quoted arguments
  const quotedMatch = query.match(/"([^"]+)"/);
  if (quotedMatch) {
    if (intent === 'search-text') entities['pattern'] = quotedMatch[1]!;
    else if (intent === 'git-commit') entities['message'] = quotedMatch[1]!;
    else entities['name'] = quotedMatch[1]!;
  }

  // Extract port numbers
  const portMatch = query.match(/port\s+(\d+)/i);
  if (portMatch) entities['port'] = portMatch[1]!;

  // Extract host names
  const hostMatch = query.match(/(?:to|from|at)\s+([\w.-]+\.\w+)/i);
  if (hostMatch) entities['host'] = hostMatch[1]!;

  // Extract PIDs
  const pidMatch = query.match(/(?:pid|process)\s+(\d+)/i);
  if (pidMatch) entities['pid'] = pidMatch[1]!;
}

function getAlternativeCommands(intent: string, context: NLContext): string[] {
  const alternatives: Record<string, string[]> = {
    'list-files': ['ls', 'ls -la', 'find . -maxdepth 1', 'tree -L 1'],
    'find-file': ['find . -name', 'locate', 'fd'],
    'search-text': ['grep -r', 'rg', 'ag'],
    'disk-usage': ['du -sh', 'ncdu', 'df -h'],
    'process-list': ['ps aux', 'top', 'htop', 'pgrep'],
    'run-tests': ['npm test', 'yarn test', 'pnpm test', 'make test'],
    'start-server': ['npm run dev', 'npm start', 'yarn dev'],
  };
  return alternatives[intent] ?? [];
}

function isSubsequence(sub: string, str: string): boolean {
  let j = 0;
  for (let i = 0; i < str.length && j < sub.length; i++) {
    if (str[i] === sub[j]) j++;
  }
  return j === sub.length;
}

function makeSuggestion(
  command: string,
  description: string,
  confidence: number,
  source: CommandSuggestion['source']
): CommandSuggestion {
  return {
    command,
    description,
    confidence,
    source,
    risk: assessCommandRisk(command),
    isPersonalized: false,
  };
}

function deduplicateSuggestions(suggestions: CommandSuggestion[]): CommandSuggestion[] {
  const seen = new Set<string>();
  const result: CommandSuggestion[] = [];
  for (const s of suggestions) {
    if (!seen.has(s.command)) {
      seen.add(s.command);
      result.push(s);
    }
  }
  return result;
}

function extractRelatedCommands(error: string): string[] {
  const commands: string[] = [];
  const cmdMatch = error.match(/`([^`]+)`/g);
  if (cmdMatch) {
    for (const m of cmdMatch) {
      commands.push(m.replace(/`/g, ''));
    }
  }
  return commands;
}
